import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from 'react';
import Helmet from 'react-helmet';
import { FormattedMessage, defineMessages } from 'react-intl';
import { QRCodeSVG } from 'qrcode.react';
import styled from 'styled-components';
import logoSrc from 'assets/Home/header__logo.svg';
import appleIconSrc from 'assets/HealthDownload/apple_icon.svg';
import playIconSrc from 'assets/HealthDownload/play_icon.svg';
import wordmarkSrc from 'assets/HealthDownload/sweatcoin_wordmark.svg';
import analytics from 'utils/analytics';
import { useInviteEssentials } from 'routes/InvitePages/utils/inviteEssentials';
import { link as storeLink } from 'routes/Home/components/AppLinks';
const messages = defineMessages({
    title: 'Your Journey{br}Starts Here',
    subtitle: 'Download the Sweatcoin App to start your programme',
    downloadButton: 'Download the app',
    getItOn: 'Get it on',
    appStore: 'App Store',
    playStore: 'Play Store',
    scanQr: 'Scan with{br}your phone',
});
const HealthDownload = (props) => {
    const searchParams = new URLSearchParams(props.location.search);
    const uuid = searchParams.get('uuid') || '';
    const { linkData: { getRedirectLink, linkQuery }, tracking: { channel, platform, feature, campaign }, config: { origin }, } = useInviteEssentials({
        deeplink_path: `/nn-programme-enroll/${uuid}`,
        feature: 'health_download',
    });
    const adjustLink = (linkQuery === null || linkQuery === void 0 ? void 0 : linkQuery.link) || '';
    function handleClick(event) {
        event.preventDefault();
        void analytics.track('HealthDownload', 'redirect', {
            origin,
            channel,
            platform,
            feature,
            campaign,
            uuid,
        });
        window.location = getRedirectLink();
    }
    return (_jsxs(Page, { children: [_jsx(Helmet, { meta: [{ name: 'event-id', content: 'health-download-page' }], link: [
                    {
                        rel: 'stylesheet',
                        href: 'https://fonts.googleapis.com/css2?family=Inter+Display:wght@400;700&display=swap',
                    },
                ] }), _jsxs(TopBar, { children: [_jsx(Logo, { src: logoSrc, alt: "Sweatcoin" }), _jsxs(StoreButtons, { children: [_jsxs(StoreButton, { href: storeLink('AppStore'), target: "_blank", rel: "noopener noreferrer", children: [_jsx(StoreIcon, { src: appleIconSrc, alt: "" }), _jsxs(StoreButtonText, { children: [_jsx(StoreButtonLabel, { children: _jsx(FormattedMessage, Object.assign({}, messages.getItOn)) }), _jsx(StoreButtonName, { children: _jsx(FormattedMessage, Object.assign({}, messages.appStore)) })] })] }), _jsxs(StoreButton, { href: storeLink('Google Play'), target: "_blank", rel: "noopener noreferrer", children: [_jsx(StoreIcon, { src: playIconSrc, alt: "" }), _jsxs(StoreButtonText, { children: [_jsx(StoreButtonLabel, { children: _jsx(FormattedMessage, Object.assign({}, messages.getItOn)) }), _jsx(StoreButtonName, { children: _jsx(FormattedMessage, Object.assign({}, messages.playStore)) })] })] })] })] }), _jsxs(Content, { children: [_jsx(Title, { children: _jsx(FormattedMessage, Object.assign({}, messages.title, { values: { br: _jsx("br", {}) } })) }), _jsx(Subtitle, { children: _jsx(FormattedMessage, Object.assign({}, messages.subtitle)) }), _jsx(MobileOnly, { children: _jsx(DownloadButton, { href: adjustLink, onClick: handleClick, children: _jsx(FormattedMessage, Object.assign({}, messages.downloadButton)) }) }), _jsx(DesktopOnly, { children: _jsxs(QRCard, { children: [_jsx(QRCodeWrapper, { children: _jsx(QRCodeSVG, { value: adjustLink, size: 192, bgColor: "transparent", fgColor: "#000000" }) }), _jsx(QRLabel, { children: _jsx(FormattedMessage, Object.assign({}, messages.scanQr, { values: { br: _jsx("br", {}) } })) })] }) })] }), _jsx(WordmarkContainer, { children: _jsx(Wordmark, { src: wordmarkSrc, alt: "" }) })] }));
};
const BREAKPOINT = '768px';
const Page = styled.section `
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background: radial-gradient(ellipse at center top, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0) 50%),
    #101010;
  color: #ffffff;
  position: relative;
  overflow: hidden;
`;
const TopBar = styled.header `
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 52px 16px 0;

  @media (min-width: ${BREAKPOINT}) {
    justify-content: space-between;
    padding: 90px 90px 0;
  }
`;
const Logo = styled.img `
  height: 32px;
  object-fit: contain;

  @media (min-width: ${BREAKPOINT}) {
    height: 56px;
  }
`;
const StoreButtons = styled.div `
  display: none;
  gap: 16px;

  @media (min-width: ${BREAKPOINT}) {
    display: flex;
  }
`;
const StoreButton = styled.a `
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 20px;
  background: #ffffff;
  border-radius: 36px;
  text-decoration: none;
  backdrop-filter: blur(18px);
`;
const StoreIcon = styled.img `
  width: 20px;
  height: 20px;
`;
const StoreButtonText = styled.div `
  display: flex;
  flex-direction: column;
  gap: 2px;
  text-align: center;
`;
const StoreButtonLabel = styled.span `
  font-family: 'Inter Display', sans-serif;
  font-weight: 400;
  font-size: 12px;
  line-height: 1.17;
  letter-spacing: -0.12px;
  color: #0f0f0f;
`;
const StoreButtonName = styled.span `
  font-family: 'Inter Display', sans-serif;
  font-weight: 700;
  font-size: 14px;
  line-height: 1.17;
  letter-spacing: -0.14px;
  color: #0f0f0f;
`;
const Content = styled.div `
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 56px 32px 0;

  @media (min-width: ${BREAKPOINT}) {
    padding: 72px 90px 0;
  }
`;
const Title = styled.h1 `
  font-family: 'Inter Display', sans-serif;
  font-weight: 700;
  font-size: 52px;
  line-height: 1.05;
  letter-spacing: -1.56px;
  text-align: center;
  color: #ffffff;
  margin: 0;

  @media (min-width: ${BREAKPOINT}) {
    font-size: 84px;
    letter-spacing: -3.36px;
  }
`;
const Subtitle = styled.p `
  font-family: 'Inter Display', sans-serif;
  font-weight: 700;
  font-size: 14px;
  line-height: 1.2;
  letter-spacing: -0.14px;
  color: rgba(255, 255, 255, 0.5);
  text-align: center;
  margin: 20px 0 0;

  @media (min-width: ${BREAKPOINT}) {
    font-size: 20px;
    letter-spacing: -0.2px;
    margin-top: 28px;
  }
`;
const MobileOnly = styled.div `
  @media (min-width: ${BREAKPOINT}) {
    display: none;
  }
`;
const DesktopOnly = styled.div `
  display: none;

  @media (min-width: ${BREAKPOINT}) {
    display: block;
  }
`;
const DownloadButton = styled.a `
  && {
    display: flex;
    align-items: center;
    justify-content: center;
    margin-top: 32px;
    padding: 16px 32px;
    background: #ffffff;
    border-radius: 36px;
    font-family: 'Inter Display', sans-serif;
    font-weight: 700;
    font-size: 14px;
    line-height: 1.2;
    letter-spacing: -0.14px;
    color: #0f0f0f;
    text-decoration: none;
    backdrop-filter: blur(18px);
  }
`;
const QRCard = styled.div `
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 32px;
  margin-top: 40px;
  padding: 40px 32px 32px;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%),
    linear-gradient(176.3deg, rgba(17, 17, 17, 0) 5.65%, rgba(17, 17, 17, 0.3) 97.08%);
  backdrop-filter: blur(47px);
`;
const QRCodeWrapper = styled.div `
  background: #ffffff;
  border-radius: 8px;
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
`;
const QRLabel = styled.p `
  font-family: 'Inter Display', sans-serif;
  font-weight: 700;
  font-size: 20px;
  line-height: 1.2;
  letter-spacing: -0.2px;
  color: rgba(255, 255, 255, 0.5);
  text-align: center;
  margin: 0;
`;
const WordmarkContainer = styled.div `
  display: flex;
  justify-content: center;
  padding-bottom: 120px;
  margin-top: auto;
`;
const Wordmark = styled.img `
  width: 84%;
  max-width: 1328px;
  height: auto;
  @keyframes slideUpFadeIn {
    from {
      opacity: 0;
      transform: translateY(60px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  animation: slideUpFadeIn 1.2s ease-out forwards;
  opacity: 0;
`;
export default HealthDownload;
